import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, Search, Loader2, FolderKanban, Database } from 'lucide-react';
import { toast } from 'sonner';
import apiFetch from '../../utils/apiFetch';

const fmtSize = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Auswahl-Dialog für E-Mail-Anhänge aus dem CRM oder einem Projekt.
 * mode: 'crm' | 'project' (Dokumente eines Projekts)
 * Mit addressId/projectId (E-Mail aus einer CRM-Adresse bzw. einem Projekt) zeigt 'crm' nur die
 * Dokumente dieser Adresse und dieses Projekts, 'project' nur die Projekte der Adresse
 * (Wunsch Ralph, 02.11.2026). Ohne Bezug (freie E-Mail) wie bisher alle Dokumente.
 * onSelect(docs[]) liefert die ausgewählten Dokument-Objekte (id, name, ...).
 */
export default function EmailDocumentPicker({ open, onClose, mode = 'crm', projectId = null, addressId = null, addressName = '', onSelect }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(projectId || '');

  const loadProjects = useCallback(async () => {
    try {
      // Mit Adresse: nur die Projekte dieser Adresse
      const r = await apiFetch(addressId ? `/projects?address_id=${encodeURIComponent(addressId)}&limit=200` : '/projects?limit=200');
      const d = await r.json();
      const items = Array.isArray(d) ? d : (d.projects || d.items || []);
      setProjects(items);
      if (!projectId && items.length && !activeProject) setActiveProject(items[0].id);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, addressId]);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    setDocs([]);
    try {
      let url = '/documents/all?limit=300';
      if (mode !== 'project' && (addressId || projectId)) {
        // Nur Dokumente der Adresse und/oder des Projekts dieser E-Mail
        const p = new URLSearchParams({ limit: '300' });
        if (addressId) p.set('address_id', addressId);
        if (projectId) p.set('project_id', projectId);
        url = `/documents/all?${p.toString()}`;
      }
      if (mode === 'project') {
        if (!activeProject) { setLoading(false); return; }
        url = `/documents/all?project_id=${activeProject}&limit=300`;
      }
      const r = await apiFetch(url);
      const d = await r.json();
      const items = Array.isArray(d) ? d : (d.documents || []);
      // Nur Dokumente mit lokalem Pfad (downloadbar) anbieten.
      // Cloud-Only Dokumente (Dropbox/Google Drive) haben zwar `filename`, aber
      // kein `file_path` und würden beim Herunterladen fehlschlagen.
      const gesehen = new Set();
      setDocs(items.filter((x) => x.file_path && !gesehen.has(x.id) && gesehen.add(x.id)));
    } catch {
      toast.error('Dokumente konnten nicht geladen werden');
    }
    setLoading(false);
  }, [mode, activeProject, addressId, projectId]);

  useEffect(() => {
    if (!open) return;
    setSelected({});
    setSearch('');
    setActiveProject(projectId || '');
    if (mode === 'project') loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, projectId]);

  useEffect(() => {
    if (open) loadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, activeProject]);

  const toggle = (doc) => setSelected((p) => ({ ...p, [doc.id]: p[doc.id] ? undefined : doc }));

  const filtered = docs.filter((d) => {
    const name = (d.name || d.title || d.original_filename || d.filename || '').toLowerCase();
    return !search || name.includes(search.toLowerCase());
  });

  const chosen = Object.values(selected).filter(Boolean);

  const confirm = () => {
    if (!chosen.length) { toast.error('Bitte mindestens ein Dokument wählen'); return; }
    onSelect?.(chosen);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col z-[400]" overlayClassName="z-[400]" data-testid="email-doc-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {mode === 'project' ? <FolderKanban className="w-4 h-4 text-blue-600" /> : <Database className="w-4 h-4 text-blue-600" />}
            {mode === 'project' ? 'Projekt-Dokumente anhängen' : 'CRM-Dokumente anhängen'}
          </DialogTitle>
        </DialogHeader>
        {(addressId || projectId) && (
          <p className="text-xs text-gray-500 -mt-2" data-testid="doc-picker-bereich">
            {mode === 'project'
              ? `Nur Projekte${addressName ? ` von ${addressName}` : ' dieser Adresse'}`
              : `Nur Dokumente ${addressId ? `der Adresse${addressName ? ` „${addressName}“` : ''}` : ''}${addressId && projectId ? ' und ' : ''}${projectId ? 'des Projekts' : ''}`}
          </p>
        )}

        {mode === 'project' && (
          <div>
            <label className="text-xs text-gray-600 block mb-1">Projekt</label>
            <Select value={activeProject} onValueChange={setActiveProject}>
              <SelectTrigger data-testid="doc-picker-project-select"><SelectValue placeholder="Projekt wählen…" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name || p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Dokument suchen…" className="pl-8 h-9" data-testid="doc-picker-search" />
        </div>

        <div className="flex-1 overflow-y-auto border rounded-lg divide-y min-h-[200px]" data-testid="doc-picker-list">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-2">
              <Loader2 className="w-6 h-6 animate-spin" /><span className="text-sm">Lädt…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-2">
              <FileText className="w-8 h-8" />
              <span className="text-sm">{(addressId || projectId) && mode !== 'project' ? 'Diese Adresse bzw. dieses Projekt hat noch keine Dokumente' : mode === 'project' && addressId && !projects.length ? 'Diese Adresse hat keine Projekte' : 'Keine Dokumente gefunden'}</span>
            </div>
          ) : (
            filtered.map((d) => {
              const name = d.name || d.title || d.original_filename || d.filename;
              const isSel = !!selected[d.id];
              return (
                <label key={d.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-blue-50 ${isSel ? 'bg-blue-50' : ''}`} data-testid={`doc-picker-item-${d.id}`}>
                  <Checkbox checked={isSel} onCheckedChange={() => toggle(d)} />
                  <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-sm flex-1 truncate">{name}</span>
                  <span className="text-xs text-gray-400 shrink-0">{fmtSize(d.size || d.file_size)}</span>
                </label>
              );
            })
          )}
        </div>

        <DialogFooter className="flex items-center sm:justify-between gap-2">
          <span className="text-xs text-gray-500">{chosen.length} ausgewählt</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button onClick={confirm} disabled={!chosen.length} data-testid="doc-picker-confirm">Anhängen</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
